import React, { useCallback, useState } from 'react';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Pick a case by citation number or client name.
 *
 * Built for the bulk-notice review screen, where automatic matching can and does come back empty —
 * a notice whose number is illegible, or a client with a namesake. Without a way to assign the case
 * by hand those notices would be unusable, which would push staff straight back to the manual
 * routine this feature replaces.
 *
 * Deliberately search-driven rather than a dropdown: there are thousands of cases, and the reviewer
 * is looking at the number printed on the page in front of them.
 *
 * Each option carries the court (Ashley, Sep 1 2026). She reported that the citation number fails to
 * match on roughly four notices in five, which means this picker — the fallback — is doing most of
 * the work, and picking by name alone cannot separate two clients with similar names. The court is
 * the field that tells them apart, and it is repeated on the confirmation chip so a wrong pick is
 * visible after selection, not only during it.
 */

export interface CaseSelection {
  ticketGuid: string;
  ticketNumber: string;
  clientName: string;
  /** Null when the case has no court on record — shown as "no court on record", never blank. */
  courtName: string | null;
}

interface TicketSearchResult {
  ticket: { Ticket_guid: string; Ticket_Number: string };
  user?: { User_FirstName?: string; User_LastName?: string };
  court?: { Court_Name?: string | null } | null;
}

interface CasePickerProps {
  label?: string;
  /** What is currently chosen, if anything — shown as a chip beside the search box. */
  selectedLabel: string | null;
  onSelect: (selection: CaseSelection | null) => void;
}

function apiHost(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

/**
 * Search by citation number first; if that returns nothing, treat the text as a name. Two narrow
 * searches beat one broad one here — the reviewer usually has the exact number, and a name search
 * on a common surname would bury it.
 *
 * The name search matches any part of the name, first or last (Ashley, Aug 18): typing "Ferrell"
 * used to find nothing because only the surname was searched.
 */
async function searchCases(term: string): Promise<CaseSelection[]> {
  const trimmed = term.trim();
  if (trimmed.length < 2) return [];

  const request = async (params: Record<string, string>): Promise<CaseSelection[]> => {
    const query = new URLSearchParams({ ...params, size: '10' });
    const response = await fetch(`${apiHost()}/api/v1/tickets?${query.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) return [];
    const results: TicketSearchResult[] = await response.json();
    return results
      .filter((result) => result?.ticket?.Ticket_guid)
      .map((result) => ({
        ticketGuid: result.ticket.Ticket_guid,
        ticketNumber: result.ticket.Ticket_Number || '(no citation number)',
        clientName:
          `${result.user?.User_FirstName ?? ''} ${result.user?.User_LastName ?? ''}`.trim() ||
          'Unknown client',
        courtName: result.court?.Court_Name?.trim() || null,
      }));
  };

  const byNumber = await request({ Ticket_Number: trimmed });
  if (byNumber.length > 0) return byNumber;

  return request({ User_Name: trimmed });
}

const CasePicker: React.FC<CasePickerProps> = ({ label = 'Case', selectedLabel, onSelect }) => {
  const [options, setOptions] = useState<CaseSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');

  const search = useCallback(async (value: string): Promise<void> => {
    setTerm(value);
    if (value.trim().length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      setOptions(await searchCases(value));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <Autocomplete
        sx={{ minWidth: 340 }}
        size="small"
        options={options}
        loading={loading}
        filterOptions={(x) => x} // the server already filtered; do not filter twice
        getOptionLabel={(option) =>
          `${option.ticketNumber} — ${option.clientName} — ${option.courtName ?? 'no court on record'}`
        }
        isOptionEqualToValue={(a, b) => a.ticketGuid === b.ticketGuid}
        noOptionsText={
          term.trim().length < 2 ? 'Type a citation number or client name' : 'No cases found'
        }
        onChange={(_event, value) => onSelect(value)}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') void search(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Citation number or client name"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {selectedLabel ? (
        <Chip color="primary" variant="outlined" label={selectedLabel} onDelete={() => onSelect(null)} />
      ) : (
        <Typography variant="body2" color="error">
          No case picked
        </Typography>
      )}
    </Stack>
  );
};

export default CasePicker;
